<template>
  <b-modal ref="modal" :title="msg('Upload Signed Contract')" size="md" hide-footer @hidden="onHidden">
    <template v-if="contract">
      <div class="text-center mb-1">
        <b-badge variant="light-info" class="mb-50">{{ contract.contractNumber }}</b-badge>
        <p class="text-muted small mb-0">{{ contract.clientCompanyName }}</p>
      </div>

      <!-- Upload Zone -->
      <div
          class="upload-zone"
          :class="{ 'upload-zone--active': dragging, 'upload-zone--has-file': selectedFile }"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
          @click="$refs.fileInput.click()"
      >
        <input ref="fileInput" type="file" accept=".pdf" class="d-none" @change="onFileSelected"/>

        <template v-if="!selectedFile">
          <feather-icon icon="UploadCloudIcon" size="40" class="text-primary mb-75"/>
          <p class="font-weight-bold mb-25">{{ msg('Drop PDF here or click to upload') }}</p>
          <small class="text-muted">{{ msg('Only PDF files accepted') }}</small>
        </template>

        <template v-else>
          <feather-icon icon="FileIcon" size="36" class="text-success mb-50"/>
          <p class="font-weight-bold mb-0">{{ selectedFile.name }}</p>
          <small class="text-muted">{{ formatSize(selectedFile.size) }}</small>
          <b-button variant="flat-danger" size="sm" class="mt-50" @click.stop="clearFile">
            <feather-icon icon="XIcon" size="13" class="mr-25"/>{{ msg('Remove') }}
          </b-button>
        </template>
      </div>

      <!-- Signing Confirmation -->
      <div v-if="selectedFile" class="mt-1">
        <div class="signing-question p-1 rounded">
          <div class="d-flex align-items-center mb-75">
            <feather-icon icon="HelpCircleIcon" size="18" class="text-warning mr-50"/>
            <span class="font-weight-bold">{{ msg('Was the contract signed successfully?') }}</span>
          </div>
          <div class="d-flex" style="gap:10px">
            <b-button
                :variant="signingSuccessful === true ? 'success' : 'outline-success'"
                size="sm" @click="signingSuccessful = true"
            >
              <feather-icon icon="CheckIcon" size="14" class="mr-25"/>{{ msg('Yes, signed') }}
            </b-button>
            <b-button
                :variant="signingSuccessful === false ? 'warning' : 'outline-warning'"
                size="sm" @click="signingSuccessful = false"
            >
              <feather-icon icon="XIcon" size="14" class="mr-25"/>{{ msg('No, not signed') }}
            </b-button>
          </div>
          <small v-if="signingSuccessful === true" class="text-success mt-50 d-block">
            {{ msg('The contract status will be updated to SIGNED.') }}
          </small>
          <small v-if="signingSuccessful === false" class="text-warning mt-50 d-block">
            {{ msg('The file will be uploaded for reference but the status remains SENT.') }}
          </small>
        </div>
      </div>

      <!-- Upload Button -->
      <div class="mt-1 text-center">
        <b-button
            variant="primary"
            :disabled="!selectedFile || signingSuccessful === null || uploading"
            @click="upload"
        >
          <b-spinner v-if="uploading" small class="mr-50"/>
          <feather-icon v-else icon="UploadIcon" size="14" class="mr-25"/>
          {{ uploading ? msg('Uploading...') : msg('Upload') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'UploadSignedModal',
  data() {
    return {
      contract: null,
      selectedFile: null,
      signingSuccessful: null,
      dragging: false,
      uploading: false,
    }
  },
  methods: {
    open(contract) {
      this.contract = contract
      this.selectedFile = null
      this.signingSuccessful = null
      this.$refs.modal.show()
    },
    onHidden() {
      this.contract = null
      this.selectedFile = null
      this.signingSuccessful = null
      this.dragging = false
    },
    onFileSelected(e) {
      const files = e.target.files
      if (files && files.length > 0) {
        this.selectedFile = files[0]
      }
    },
    onDrop(e) {
      this.dragging = false
      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        const file = files[0]
        if (file.type === 'application/pdf') {
          this.selectedFile = file
        }
      }
    },
    clearFile() {
      this.selectedFile = null
      this.signingSuccessful = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
    async upload() {
      if (!this.selectedFile || this.signingSuccessful === null) return
      this.uploading = true
      try {
        await this.$store.dispatch('contract/uploadSignedPdf', {
          id: this.contract.id,
          file: this.selectedFile,
          signingSuccessful: this.signingSuccessful,
        })
        this.$emit('uploaded')
        this.$refs.modal.hide()
      } finally {
        this.uploading = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.upload-zone {
  border: 2px dashed #d1d5db; border-radius: 12px; padding: 30px; text-align: center;
  cursor: pointer; transition: all .2s ease;
  &:hover, &--active { border-color: #4f46e5; background: rgba(79,70,229,.04); }
  &--has-file { border-color: #10b981; background: rgba(16,185,129,.04); }
}
.dark-layout .upload-zone {
  border-color: #555; background: #1e1e2e;
  &:hover, &--active { border-color: #7c3aed; background: rgba(124,58,237,.08); }
  &--has-file { border-color: #10b981; background: rgba(16,185,129,.08); }
}

.signing-question {
  background: #fefce8; border: 1px solid #fde68a;
}
.dark-layout .signing-question {
  background: #292524; border-color: #78716c;
}
</style>
